<template>
  <b-modal
    v-model="showModal"
    title="Training Details"
    size="lg"
    hide-footer
    @hidden="handleClose"
  >
    <div v-if="event" class="event-detail">
      <!-- Course Info -->
      <div class="mb-4">
        <h5 class="text-primary">
          <i class="fa fa-book mr-2"></i>
          {{ event.courseName }}
        </h5>
        <p class="text-muted mb-2">
          <strong>Batch:</strong> {{ event.batchName }}
        </p>
      </div>

      <!-- Date & Time -->
      <div class="mb-4">
        <h6 class="mb-2">
          <i class="fa fa-clock mr-2"></i>
          Schedule
        </h6>
        <p class="mb-1"><strong>Start:</strong> {{ event.startDate }}</p>
        <p class="mb-0"><strong>End:</strong> {{ event.endDate }}</p>
      </div>

      <!-- Provider & Location -->
      <div class="mb-4">
        <h6 class="mb-2">
          <i class="fa fa-info-circle mr-2"></i>
          Information
        </h6>
        <p class="mb-1">
          <strong>Training Type:</strong>
          <span
            class="badge ml-2"
            :class="getTrainingTypeBadgeClass(event.trainingType)"
          >
            <i
              :class="getTrainingTypeIcon(event.trainingType)"
              class="mr-1"
            ></i>
            {{ getTrainingTypeText(event.trainingType) }}
          </span>
        </p>
        <p class="mb-1">
          <strong>Provider:</strong>
          <span
            class="badge ml-2"
            :style="{
              backgroundColor:
                event.provider === 'GEMS Univ' ? '#0066CC' : '#FF6B35',
            }"
          >
            {{ event.provider }}
          </span>
        </p>
        <p class="mb-1">
          <strong>Location:</strong> {{ event.location || "TBA" }}
        </p>
        <p class="mb-1"><strong>Course Type:</strong> {{ event.courseType }}</p>
        <p class="mb-1" v-if="showCompany">
          <strong>Company:</strong> {{ event.company }}
        </p>
        <p class="mb-0" v-if="event.isMandatory">
          <span class="badge badge-danger">
            <i class="fa fa-exclamation-triangle mr-1"></i>
            Mandatory
          </span>
        </p>
      </div>

      <!-- Participant Count (Admin/Manager) -->
      <div v-if="showParticipantCount" class="mb-4">
        <h6 class="mb-2">
          <i class="fa fa-users mr-2"></i>
          Participants
        </h6>
        <p class="mb-0">
          <strong>Total Enrolled:</strong>
          {{ event.participantCount }} participants
        </p>
      </div>

      <!-- Actions -->
      <div class="d-flex justify-content-end align-items-center flex-wrap mt-4 pt-3 border-top">
        <b-button
          v-for="action in enrollmentActions"
          :key="action.key"
          variant="primary"
          class="mr-2 mb-1"
          @click="goToEnrollment(action)"
        >
          {{ action.label }}
        </b-button>
        <b-button variant="secondary" class="mb-1" @click="$emit('close')">
          Close
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import {
  buildEnrollmentQuery,
  getEnrollmentActions,
  hasValidEnrollmentRef,
} from "../utils/enrollment-links";

export default {
  name: "TrainingDetailModal",

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    event: {
      type: Object,
      default: null,
    },
    userRole: {
      type: String,
      default: "employee", // 'employee' | 'manager' | 'admin'
      validator: (value) => ["employee", "manager", "admin"].includes(value),
    },
  },

  computed: {
    ...mapGetters("users", ["profileData"]),

    /**
     * Local v-model for modal
     */
    showModal: {
      get() {
        return this.show;
      },
      set(value) {
        if (!value) {
          this.$emit("close");
        }
      },
    },

    /**
     * Show company info (admin only)
     */
    showCompany() {
      return this.userRole === "admin";
    },

    /**
     * Show participant count (admin/manager)
     */
    showParticipantCount() {
      return this.userRole === "admin" || this.userRole === "manager";
    },

    enrollmentActions() {
      if (!this.event || !hasValidEnrollmentRef(this.event)) {
        return [];
      }
      return getEnrollmentActions(this.profileData);
    },
  },

  methods: {
    /**
     * Handle modal close
     */
    handleClose() {
      this.$emit("close");
    },

    goToEnrollment(action) {
      if (!action?.routeName || !this.event) return;

      this.$emit("close");
      this.$router
        .push({
          name: action.routeName,
          query: buildEnrollmentQuery(this.event),
        })
        .catch(() => {});
    },

    /**
     * Format date for display
     */
    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      return d.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    /**
     * Get training type badge class
     */
    getTrainingTypeBadgeClass(type) {
      const typeMap = {
        structured: "badge-primary",
        structured_learning: "badge-primary",
        social: "badge-success",
        social_learning: "badge-success",
        experiential: "badge-warning",
        experiential_learning: "badge-warning",
      };
      return typeMap[type] || "badge-secondary";
    },

    /**
     * Get training type icon
     */
    getTrainingTypeIcon(type) {
      const iconMap = {
        structured: "fa fa-graduation-cap",
        structured_learning: "fa fa-graduation-cap",
        social: "fa fa-users",
        social_learning: "fa fa-users",
        experiential: "fa fa-project-diagram",
        experiential_learning: "fa fa-project-diagram",
      };
      return iconMap[type] || "fa fa-book";
    },

    /**
     * Get training type text
     */
    getTrainingTypeText(type) {
      const textMap = {
        structured: "Structured Learning",
        structured_learning: "Structured Learning",
        social: "Social Learning",
        social_learning: "Social Learning",
        experiential: "Experiential Learning",
        experiential_learning: "Experiential Learning",
      };
      return textMap[type] || type;
    },
  },
};
</script>

<style scoped>
.event-detail h5 {
  font-weight: 600;
}

.event-detail h6 {
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 0.5rem;
}

.badge-lg {
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
}
</style>
